import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from "@mui/material";
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { HealthAndSafety, KeyOff, WarningRounded } from "@mui/icons-material";
import { execBookingAction, useBookingByIdWithMetaQueryObserver, getRaCheckInCheckOut, getCheckUploadedAttachment, checkSubstitutionByRa} from "../../../api/services";
import { BookingAction, BookingScope, RouteParams } from "../../../types";
import { InsertBookingStateAction } from "../../insert/types";
import AssignVehicleAction from "./assign-vehicle/AssignVehicleAction";
import BlacklistCheckAction from "./blacklist-check/BlacklistCheckAction";
import BookAction from "./book/BookAction";
import ChangeStatusAction from "./change-status/ChangeStatusAction";
import DraftEditAction from "./edit-draft/DraftEdit";
import getResendMail from "../../../api/services/getResendMail";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import useDialog from "../../../hooks/useDialog";
import DialogCloseButton from "../../../components/DialogCloseButton";
import { useHasScope } from "../../../../../config/globalConfig";
import { CHECKIN } from "../types";
import { checkExistSubscription } from "../../../api/services/checkExistSubscription";

const ActionsBar = () => {

    const {t} = useTranslation('booking');

    const navigate = useNavigate();

    const {dispatchGlobalData} = useContext(GlobalContext);

    const hasScope = useHasScope;

    const {id} = useParams<RouteParams>();

    const bookingQuery = useBookingByIdWithMetaQueryObserver(id);

    const editDialog = useDialog();
    const newBookingDialog = useDialog();
    const checkInSubstitutionVehicleAvailabilityDialog = useDialog();
    const checkInCarswapVehicleAvailabilityDialog = useDialog();
    const checkUploadedAttachmentDialog = useDialog();

    const handleCreateRAClick = useCallback(() => {
        navigate(`/network/create-ra/${id}`);
    }, [id]);

    const handleCreateRARecoveryClick = useCallback(() => {
        navigate(`/network/create-ra/${id}?recoveryMode=1`);
    }, [id]);

    const handleCreateRAKeylessClick = useCallback(() => {
        navigate(`/network/create-ra/${id}?keyless=1`);
    }, [id]);

    const handleCreateRaRceClick = useCallback(() => {
        navigate(`/network/create-ra/${id}?rce=1`);
    }, [id]);

    const handleCloneClick = useCallback((cloneWithRatePlan = false) => {
        navigate('/booking/insert', {
            state: {
                booking: bookingQuery.data?.booking,
                action: cloneWithRatePlan ? InsertBookingStateAction.CopyWithRatePlan : InsertBookingStateAction.Copy
            }
        });
    }, [bookingQuery.data]);
    const handleNewBookingClick = useCallback(() => {
        navigate('/booking/insert');
    }, []);

    const handleCreateRecoverySubstitutionClick = useCallback(() => {
        navigate(`/network/create-ra/${id}?recoveryMode=1`);
    }, [id]);

    const handleResendMailClick = useCallback(() => {
        getResendMail(id)
            .then(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.success.default'),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            })
            .catch(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            })
    }, []);

    const handleCheckExistSubscription = useCallback(()=> {
        const subscriptionNumber = bookingQuery?.data?.booking?.subscriptionNumber;
        
        if(!!subscriptionNumber) {
            checkExistSubscription(subscriptionNumber).then(response => {
                if(response) { 
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('message.error.ongoing_ra'),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });

                } else verifyCarSwap();

            }).catch(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            });

        } else verifyCarSwap();
        

    }, [bookingQuery.data]);

    const verifyCarSwap = useCallback(() => {
        !!bookingQuery.data?.booking?.carSwap ? verifyCheckIn('carSwap') : handleCreateRAClick();
    }, [bookingQuery.data]);

    const verifyCheckIn = useCallback((mode: 'substitution' | 'carSwap' | 'recoverySubstitution') => {
        const idRa = bookingQuery?.data?.booking?.parentRa;
        if (!idRa) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.default'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
            return;
        }
        getRaCheckInCheckOut({id: idRa, outin: CHECKIN, recoveryMode: mode === 'recoverySubstitution'})
            .then( response => {
                if (response.executed) {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('message.success.default'),
                                type: snackbarTypes.success,
                                show: true,
                                autoClose: true
                            }
                        }
                    });
                    if(mode === 'recoverySubstitution'){
                        handleCreateRecoverySubstitutionClick();
                    }else{
                        handleCreateRAClick();
                    }
                } else {
                    if(mode === 'substitution' || mode === 'recoverySubstitution') checkInSubstitutionVehicleAvailabilityDialog.handleShow();
                    if(mode === 'carSwap') checkInCarswapVehicleAvailabilityDialog.handleShow();
                }

            })
            .catch(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            })
    }, [[bookingQuery.data]]);

    const handleCreateSubstitution = useCallback((isRecovery: boolean) => {
        if (!!bookingQuery?.data?.booking?.rentalAgreement) {
            checkSubstitutionByRa(bookingQuery?.data?.booking?.rentalAgreement)
                .then( (response: any) => {
                    if (!response.substitution) {
                        createSubstitution(isRecovery);
                    } else {
                        dispatchGlobalData({
                            type: setSnackbar,
                            payload: {
                                snackbar: {
                                    message: t('message.error.checkSubstitution'),
                                    type: snackbarTypes.error,
                                    show: true,
                                    autoClose: true,
                                }
                            }
                        });
                    }
                })
                .catch((err: any) => {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('message.error.default'),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                })
        } else {
            createSubstitution(isRecovery);
        }
    }, [bookingQuery.data]);



    const createSubstitution = (isRecovery: boolean) => {
        if(!id) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.default'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
            return
        }
        getCheckUploadedAttachment(id)
            .then( response => {
                if (response) {
                    if(isRecovery){
                        verifyCheckIn('recoverySubstitution');
                    }else{
                        verifyCheckIn('substitution');
                    }                
                } else {
                    checkUploadedAttachmentDialog.handleShow();
                }
            })
            .catch(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            })
    };

    const handleEditClick = useCallback(() => {
        editDialog.handleShow();
    }, []);
    const handleEditCancelClick = useCallback(() => {
        newBookingDialog.handleShow();
    }, []);

    const handleConfirmClick = useCallback(() => {
        if (!bookingQuery.data) {
            return;
        }

        const bookingId = bookingQuery.data.booking.id;
        navigate(`/booking/edit/${bookingId}`);
    }, [bookingQuery.data]);

    const execBookingActionMutation = useMutation(
        execBookingAction,
        {
            onSuccess: () => {
                navigate('/booking/insert', {
                    state: {
                        booking: bookingQuery.data?.booking,
                        action: InsertBookingStateAction.Copy
                    }
                });
            },
            onError: () => {
                newBookingDialog.handleClose();
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                })
            },
        }

    );

    const handleConfirmCancelClick = useCallback(() => {
        if (!bookingQuery.data) {
            return;
        }
        execBookingActionMutation.mutate({
            bookingId: bookingQuery.data.booking.id,
            action: BookingAction.Cancel,
            subaction: 'CUSTOMER',
            note: t('message.info.edit_booking_note'),
            lastUpdateTime: bookingQuery.data.booking.requestUpdateDate
        });
    }, [bookingQuery.data]);

    const isStatusOffer = bookingQuery.data?.booking.status.startsWith("OFFER");

    return isStatusOffer ? <></> : (        
        <>
            <Stack direction="row" justifyContent="end" alignItems="center" columnGap={2} p={2}>
                <ChangeStatusAction/>
                <BookAction/>
                <DraftEditAction/>
                <BlacklistCheckAction/>
                {bookingQuery.data?.permissions.actions[BookingAction.AssignVehicle] &&
                    <AssignVehicleAction/>
                }
                {bookingQuery.data?.permissions.actions[BookingAction.Clone] &&
                    <Button onClick={() => handleCloneClick()}>
                        {t('label.clone')}
                    </Button>
                }
                {bookingQuery.data?.permissions.actions[BookingAction.CloneVoid] &&
                    <Button onClick={() => handleCloneClick(true)}>
                        {t('label.cloneWithRatePlan')}
                    </Button>
                }
                { (
                    bookingQuery.data?.permissions.actions[BookingAction.hardEdit] ||
                    (
                      bookingQuery.data?.permissions.actions[BookingAction.EditRcePending] &&
                      bookingQuery.data?.booking.rce === true
                    )
                  ) && (
                    <Button onClick={handleEditClick}>
                      {t('label.edit')}
                    </Button>
                  )
                }
                { bookingQuery.data?.permissions.actions[BookingAction.cancelNew] &&
                    <Button onClick={handleEditCancelClick}>
                        {t('label.edit')}
                    </Button>
                }
                {bookingQuery.data?.permissions.actions[BookingAction.CreateRA] &&
                    <Button onClick={handleCheckExistSubscription}>
                        {t('label.createRA')}
                    </Button>
                }
                {bookingQuery.data?.permissions.actions[BookingAction.CreateRARecovery] &&
                    <Button onClick={handleCreateRARecoveryClick} variant="outlined" startIcon={<HealthAndSafety />}>
                        {t('label.createRARecovery')}
                    </Button>
                }
                {
                    <Button onClick={handleCreateRAKeylessClick} disabled={!bookingQuery.data?.permissions.actions[BookingAction.CreateRaKeyless]} variant="outlined" startIcon={<KeyOff />}>
                        {t('label.createRaKeyless')}
                    </Button>
                }
                {
                    <Button onClick={handleCreateRaRceClick} disabled={!bookingQuery.data?.permissions.actions[BookingAction.CreateRaRce]} variant="outlined" startIcon={<KeyOff />}>
                        {t('label.createRaRce')}
                    </Button>
                }
                {bookingQuery.data?.permissions.actions[BookingAction.resendMail] &&
                    <Button onClick={handleResendMailClick}>
                        {t('label.resendMail')}
                    </Button>
                }
                {bookingQuery.data?.permissions.actions[BookingAction.CreateSubstitution] &&
                    <Button onClick={()=>handleCreateSubstitution(false)}>
                        {t('label.createSubstitution')}
                    </Button>
                }
                {/* BUTTON RECOVERY SUSTITUTION */}
                  {bookingQuery.data?.permissions.actions[BookingAction.CreateSubstitutionRecovery] &&
                    <Button onClick={()=>handleCreateSubstitution(true)}
                        variant={'outlined'}
                        color={"accent"}
                        startIcon={<HealthAndSafety />}>
                        {t('label.createSubstitutionRecovery')}
                    </Button>
                }
                { hasScope(BookingScope.InsertBooking) &&
                    <Button onClick={handleNewBookingClick}>
                        {t('label.newBooking')}
                    </Button>
                }
            </Stack>
            <Dialog
                open={editDialog.open}
                onClose={editDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton onClick={editDialog.handleClose}/>
                <DialogTitle>
                    {t("label.bookingEditing")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("message.info.edit_with_processing")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={editDialog.handleClose}>
                        {t("label.cancel")}
                    </Button>
                    <Button onClick={handleConfirmClick}>
                        {t("label.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={newBookingDialog.open}
                onClose={newBookingDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton onClick={newBookingDialog.handleClose} />
                <DialogTitle>
                    {t("label.bookingEditing")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("message.info.edit_with_cancellation")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={newBookingDialog.handleClose}>
                        {t("label.cancel")}
                    </Button>
                    <Button onClick={handleConfirmCancelClick}>
                        {t("label.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={checkInSubstitutionVehicleAvailabilityDialog.open}
                onClose={checkInSubstitutionVehicleAvailabilityDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton onClick={checkInSubstitutionVehicleAvailabilityDialog.handleClose} />
                <DialogTitle>
                    {t("label.createSubstitution")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("message.error.chekin_vehicle_before_ra_substitution")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={checkInSubstitutionVehicleAvailabilityDialog.handleClose}>
                        {t('label.ok')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={checkInCarswapVehicleAvailabilityDialog.open}
                onClose={checkInCarswapVehicleAvailabilityDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton onClick={checkInCarswapVehicleAvailabilityDialog.handleClose} />
                <DialogTitle>
                    {t("label.createRA")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("message.error.chekin_vehicle_before_ra_carswap")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={checkInCarswapVehicleAvailabilityDialog.handleClose}>
                        {t('label.ok')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={checkUploadedAttachmentDialog.open}
                onClose={checkUploadedAttachmentDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton
                    onClick={checkUploadedAttachmentDialog.handleClose}
                />
                <DialogTitle>
                    <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={1}
                    >
                        <WarningRounded color="error" />
                        {t('label.error')}
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        sx={{
                            whiteSpace: 'pre-line'
                        }}
                    >
                        {t("message.error.chek_attachment_before_ra")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={checkUploadedAttachmentDialog.handleClose}
                    >
                        {t('label.ok')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ActionsBar;
